import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { Subscription } from '@planner/types'
import {
  getSubscriptionLimit,
  calculateBillingPeriodEnd,
  getPlanFeatures,
  type SubscriptionPlan,
} from '../utils/subscriptionUtils'

interface SubscriptionStats {
  subscription: Subscription | null
  eventsCount: number
  limit: number
  remaining: number
  periodEnd: Date | null
  loading: boolean
  error: string | null
}

interface CanCreateEventResult {
  allowed: boolean
  message: string | null
  currentCount: number
  limit: number
}

export function useSubscription() {
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [eventsCount, setEventsCount] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSubscription = useCallback(async () => {
    try {
      setError(null)
      
      // Check if Supabase is configured (local dev without Supabase)
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
      const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
      const isLocalDevWithoutSupabase = process.env.NODE_ENV === 'development' && 
                        (!supabaseUrl || !supabaseAnonKey || supabaseUrl.includes('placeholder'))

      // Only use demo mode when Supabase is NOT configured at all
      if (isLocalDevWithoutSupabase) {
        setSubscription({
          id: 'demo-free',
          plan_type: 'free',
          started_at: new Date().toISOString(),
          expires_at: null,
          status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as Subscription)
        setLoading(false)
        return
      }
      
      // Call the database function to get current subscription
      const { data, error: fetchError } = await supabase.rpc(
        'get_current_subscription'
      )

      if (fetchError) {
        // If no subscription exists, default to free plan
        if (fetchError.code === 'P0001' || fetchError.message.includes('no rows')) {
          setSubscription({
            id: 'default-free',
            plan_type: 'free',
            started_at: new Date().toISOString(),
            expires_at: null,
            status: 'active',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          } as Subscription)
          setLoading(false)
          return
        }
        throw fetchError
      }

      if (data && data.length > 0) {
        setSubscription(data[0] as Subscription)
      } else {
        // Default to free plan if no subscription
        setSubscription({
          id: 'default-free',
          plan_type: 'free',
          started_at: new Date().toISOString(),
          expires_at: null,
          status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as Subscription)
      }
    } catch (err) {
      // Only log error if not in demo mode to avoid console noise
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
      const isDemoMode = process.env.NODE_ENV === 'development' && 
                        (!supabaseUrl || supabaseUrl.includes('placeholder'))
      
      if (!isDemoMode) {
        console.error('Error fetching subscription:', err)
      }
      setError(err instanceof Error ? err.message : 'Failed to fetch subscription')
      // Default to free plan on error
      setSubscription({
        id: 'default-free',
        plan_type: 'free',
        started_at: new Date().toISOString(),
        expires_at: null,
        status: 'active',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as Subscription)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchEventsCount = useCallback(async () => {
    if (!subscription) return

    try {
      // Check if Supabase is configured (demo mode detection)
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
      const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
      const isDemoMode = process.env.NODE_ENV === 'development' && 
                        (!supabaseUrl || !supabaseAnonKey || supabaseUrl.includes('placeholder'))

      // In demo mode, set count to 0
      if (isDemoMode) {
        setEventsCount(0)
        return
      }

      const { data, error: countError } = await supabase.rpc(
        'count_events_in_billing_period'
      )

      if (countError) {
        const isDemoMode = process.env.NODE_ENV === 'development' && 
                          (!supabaseUrl || supabaseUrl.includes('placeholder'))
        if (!isDemoMode) {
          console.error('Error counting events:', countError)
        }
        setEventsCount(0)
        return
      }

      setEventsCount(data || 0)
    } catch (err) {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
      const isDemoMode = process.env.NODE_ENV === 'development' && 
                        (!supabaseUrl || supabaseUrl.includes('placeholder'))
      if (!isDemoMode) {
        console.error('Error counting events:', err)
      }
      setEventsCount(0)
    }
  }, [subscription])

  const canCreateEvent = useCallback(
    async (eventDate: Date): Promise<CanCreateEventResult> => {
      try {
        // Check if Supabase is configured (demo mode detection)
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
        const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
        const isDemoMode = process.env.NODE_ENV === 'development' && 
                          (!supabaseUrl || !supabaseAnonKey || supabaseUrl.includes('placeholder'))

        // In demo mode, allow event creation (free plan limit: 1 event)
        if (isDemoMode) {
          const currentLimit = subscription ? getSubscriptionLimit(subscription.plan_type) : 1
          return {
            allowed: eventsCount < currentLimit,
            message: eventsCount >= currentLimit ? 'Osiągnięto limit wydarzeń w planie Free' : null,
            currentCount: eventsCount,
            limit: currentLimit,
          }
        }

        const { data, error: checkError } = await supabase.rpc(
          'can_create_event',
          {
            event_date_param: eventDate.toISOString(),
          }
        )

        if (checkError) {
          return {
            allowed: false,
            message: checkError.message || 'Nie można utworzyć wydarzenia',
            currentCount: 0,
            limit: 0,
          }
        }

        if (data && data.length > 0) {
          const result = data[0]
          return {
            allowed: result.allowed || false,
            message: result.message || null,
            currentCount: result.current_count || 0,
            limit: result.limit_count || 0,
          }
        }

        return {
          allowed: false,
          message: 'Nie można sprawdzić limitu wydarzeń',
          currentCount: 0,
          limit: 0,
        }
      } catch (err) {
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
        const isDemoMode = process.env.NODE_ENV === 'development' && 
                          (!supabaseUrl || supabaseUrl.includes('placeholder'))
        if (!isDemoMode) {
          console.error('Error checking event limit:', err)
        }
        return {
          allowed: false,
          message: err instanceof Error ? err.message : 'Błąd sprawdzania limitu',
          currentCount: 0,
          limit: 0,
        }
      }
    },
    [subscription, eventsCount]
  )

  useEffect(() => {
    fetchSubscription()
  }, [fetchSubscription])

  useEffect(() => {
    if (subscription) {
      fetchEventsCount()
    }
  }, [subscription, fetchEventsCount])

  const limit = subscription
    ? getSubscriptionLimit(subscription.plan_type)
    : getSubscriptionLimit('free')

  const remaining = Math.max(0, limit - eventsCount)

  const periodEnd = subscription
    ? calculateBillingPeriodEnd(new Date(subscription.started_at))
    : null

  const planType: SubscriptionPlan = subscription?.plan_type || 'free'
  const features = getPlanFeatures(planType)

  const stats: SubscriptionStats = {
    subscription,
    eventsCount,
    limit,
    remaining,
    periodEnd,
    loading,
    error,
  }

  return {
    ...stats,
    canCreateEvent,
    refetch: fetchSubscription,
    features,
    planType,
  }
}
